// Workflows list — grid of cards, each linking to the editor.

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Workflow as WorkflowIcon } from 'lucide-react';
import type { WorkflowListItem } from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge, type BadgeTone } from '@/components/primitives/Badge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { PageContainer, PageHeader } from '@/components/PageHeader';
import { listWorkflows } from '@/lib/workflows-api';
import { extractApiError } from '@/lib/api-client';
import { humanizeEnum } from '@/lib/format';

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'ok',
  PAUSED: 'warn',
  DRAFT: 'default',
};

function formatLastRun(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export function WorkflowsListPage() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['workflows'],
    queryFn: listWorkflows,
  });

  return (
    <PageContainer>
      <PageHeader
        title="Workflows"
        description="Event-driven automations: doc ingest, approvals, AI flows"
        actions={
          <Btn icon={Plus} kind="primary" onClick={() => navigate('/workflows/new')}>
            New workflow
          </Btn>
        }
      />

      {query.isPending && (
        <div className="text-[13px] text-text-3 py-10 text-center">Loading…</div>
      )}

      {query.isError && (
        <Card>
          <div
            className="px-3 py-2 rounded-md text-[12.5px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            {extractApiError(query.error)}
          </div>
        </Card>
      )}

      {query.data && query.data.length === 0 && (
        <Card>
          <Empty
            icon={WorkflowIcon}
            title="No workflows yet"
            body="Spin up your first event-driven automation to wire docs, approvals, and AI agents together."
            actions={
              <Btn icon={Plus} kind="primary" onClick={() => navigate('/workflows/new')}>
                New workflow
              </Btn>
            }
          />
        </Card>
      )}

      {query.data && query.data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
          {query.data.map((wf) => (
            <WorkflowCard
              key={wf.id}
              workflow={wf}
              onOpen={() => navigate(`/workflows/${wf.id}`)}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function WorkflowCard({
  workflow,
  onOpen,
}: {
  workflow: WorkflowListItem;
  onOpen: () => void;
}) {
  const lastRun = formatLastRun(workflow.lastRunAt);
  const tone = STATUS_TONE[workflow.status] ?? 'default';

  return (
    <Card onClick={onOpen}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="font-semibold text-[14px] text-text leading-tight">
          {workflow.name}
        </div>
        <Badge tone={tone}>{humanizeEnum(workflow.status)}</Badge>
      </div>

      <div
        className="text-[13px] text-text-2 mb-4 overflow-hidden"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          minHeight: 36,
        }}
      >
        {workflow.description || 'No description provided.'}
      </div>

      <div className="flex items-center justify-between gap-2">
        <span
          className="inline-flex items-center px-2 py-1 rounded-md text-[11.5px] text-text-2"
          style={{
            fontFamily: 'var(--font-mono)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
          }}
        >
          {workflow.triggerType}
        </span>
        <div className="text-[12px] text-text-3 text-right">
          <div>
            {workflow.stepCount} steps · {workflow.runs.toLocaleString()} runs
          </div>
          {lastRun && <div className="mt-0.5">Last run {lastRun}</div>}
        </div>
      </div>
    </Card>
  );
}
